import { dataLayer } from '@/constants';
import { QueryKeys } from '@/enums/index';
import { openNotification } from '@/helpers/mantine-notification-helper';
import { INextAuthSessionModel } from '@/interfaces/authentication';
import { IAddToCartItem, IAddToCartItemAttribute, ICartItems, IGetCartProduct } from '@/interfaces/cart';
import { IProductDetails } from '@/interfaces/product';
import { addToCart, deleteFromCart, editCartItemQuantity, getCart } from '@/services/cart.service';
import { getProduct } from '@/services/product.service';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useReactQuerySession } from './reactQuerySession';

export const useCart = (queryClient: QueryClient, session?: INextAuthSessionModel | null) => {
  return useQuery<IGetCartProduct>(
    [QueryKeys.cartItems, session?.accessToken || 'anonymous'],
    async () => {
      if (session) {
        //  return await get401();
        return await getCart(session);
      } else {
        let cartItems: IGetCartProduct = {
          Items: [],
          SubTotalValue: 0,
          TotalValue: 0,
          Warning: [],
        };
        let stringifiedCartItems = localStorage.getItem('cart');
        if (stringifiedCartItems) {
          try {
            let parsedCart = JSON.parse(stringifiedCartItems) as IGetCartProduct;
            if (parsedCart.Items) {
              let uniqueSeNames: string[] = [];
              parsedCart.Items.forEach((x) => {
                if (!uniqueSeNames.includes(x.ProductSeName)) uniqueSeNames.push(x.ProductSeName);
              });
              let listOfPromises: Promise<IProductDetails>[] = [];
              uniqueSeNames.forEach((name) => {
                listOfPromises.push(getProduct(name));
              });
              try {
                let responseOfAllProductPromises = await Promise.all(listOfPromises);
                parsedCart.SubTotalValue = 0;
                parsedCart.TotalValue = 0;
                parsedCart.Items.forEach((cartProduct) => {
                  let selectedProductResponseIndex = responseOfAllProductPromises.findIndex((x) => x.SeName === cartProduct.ProductSeName);

                  if (selectedProductResponseIndex > -1) {
                    cartProduct.AvailableColors = responseOfAllProductPromises[selectedProductResponseIndex]?.AvailableColors;
                    cartProduct.Pictures = responseOfAllProductPromises[selectedProductResponseIndex]?.ProductImageList;
                    cartProduct.ProductId = responseOfAllProductPromises[selectedProductResponseIndex]?.Id;
                    cartProduct.ProductName = responseOfAllProductPromises[selectedProductResponseIndex]?.Name;
                    cartProduct.ProductSeName = responseOfAllProductPromises[selectedProductResponseIndex]?.SeName;
                    cartProduct.ProductSku = responseOfAllProductPromises[selectedProductResponseIndex]?.Sku;
                    cartProduct.SubTotalValue = responseOfAllProductPromises[selectedProductResponseIndex].ProductPrice?.Price;
                    cartProduct.UnitPriceValue = responseOfAllProductPromises[selectedProductResponseIndex].ProductPrice?.Price;
                    cartProduct.UnitOldPrice = responseOfAllProductPromises[selectedProductResponseIndex].ProductPrice?.OldPrice;
                    //cartProduct.Variations = responseOfAllProductPromises[selectedProductResponseIndex].Variations;// this needs to persist with the local storage
                    cartProduct.IsWishListed = responseOfAllProductPromises[selectedProductResponseIndex]?.IsWishListed;
                    cartProduct.OrderMaximumQuantity = responseOfAllProductPromises[selectedProductResponseIndex]?.OrderMaximumQuantity;
                    cartProduct.OrderMinimumQuantity = responseOfAllProductPromises[selectedProductResponseIndex]?.OrderMinimumQuantity;
                    cartProduct.IsGiftVoucher = responseOfAllProductPromises[selectedProductResponseIndex]?.IsGiftVoucher;
                    cartProduct.Brand = responseOfAllProductPromises[selectedProductResponseIndex].ProductBrand?.Name;
                    cartProduct.Category = responseOfAllProductPromises[selectedProductResponseIndex]?.CategoryName;
                    cartProduct.Gender = responseOfAllProductPromises[selectedProductResponseIndex]?.Gender;
                    parsedCart.SubTotalValue += responseOfAllProductPromises[selectedProductResponseIndex].ProductPrice?.Price * cartProduct.Quantity;
                    parsedCart.TotalValue += responseOfAllProductPromises[selectedProductResponseIndex].ProductPrice?.Price * cartProduct?.Quantity;
                  }
                });
              } catch {
                console.log('cart sync failed, returning cart from local storage as is.');
              }
            }

            return parsedCart;
          } catch {
            return cartItems;
          }
        } else {
          return cartItems;
        }
      }
    },
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      retry: false,
      async onSuccess(data) {},
    },
  );
};

export const useAddToCartMutation = (addToCartCallback?: (shouldOpenCart?: boolean, shouldOpenNotification?: boolean) => void) => {
  const queryClient = useQueryClient();
  const authObject = useReactQuerySession();
  return useMutation(
    (cartItem: ICartItems) => {
      if (authObject.isAuthenticated) {
        let tempAttributes: IAddToCartItemAttribute[] = [];
        let selectedVariant = cartItem.Variations.find((x) => x.IsSelected);
        if (selectedVariant) {
          tempAttributes.push({
            key: selectedVariant.ProductAttributeId,
            value: selectedVariant.ProductAttributeValueId,
          });
        }
        const payload: IAddToCartItem = {
          productId: cartItem.ProductId,
          quantity: cartItem.Quantity,
          attributes: tempAttributes,
        };
        return addToCart(payload);
      } else {
        let cartItems: IGetCartProduct = {
          Items: [],
          SubTotalValue: 0,
          TotalValue: 0,
          Warning: [],
        };
        let stringifiedCartItems = localStorage.getItem('cart');
        if (stringifiedCartItems) {
          // update empty cart Object with Cart Items form local storage
          try {
            let tempCartItems = JSON.parse(stringifiedCartItems) as IGetCartProduct;
            if (tempCartItems.Items) {
              cartItems = tempCartItems;
            } else {
              throw Error('parsed json is corrupted');
            }
          } catch (e) {
            console.log(e, 'json parse error');
          }
        }
        let foundProduct = cartItems.Items.find((x) => x.Id === cartItem.Id);
        if (foundProduct) {
          foundProduct.Quantity += cartItem.Quantity;
        } else {
          cartItems.Items.push(cartItem);
        }
        cartItems.SubTotalValue += cartItem.UnitPriceValue * cartItem.Quantity;
        cartItems.TotalValue += cartItem.UnitPriceValue * cartItem.Quantity;

        localStorage.setItem('cart', JSON.stringify(cartItems));

        return new Promise((resolve) => resolve('Cart updated locally.'));
      }
    },
    {
      onSuccess: (data, cartItem: ICartItems) => {
        addToCartCallback?.(cartItem.shouldOpenCart, cartItem.shouldOpenNotification);
        queryClient.invalidateQueries([QueryKeys.cartItems, authObject.session?.accessToken || 'anonymous']);
        queryClient.invalidateQueries([QueryKeys.orderSummary]);
        let obj: { [key: string]: string } = {};
        if (cartItem?.Category) {
          let splittedCategories = cartItem?.Category.split(',');
          splittedCategories.forEach((category, categoryIndex) => {
            obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
          });
        }
        if (!cartItem?.IsGiftVoucher && cartItem?.Gender) {
          obj['gender'] = cartItem?.Gender;
        }
        dataLayer.push({ ecommerce: null });

        dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'BDT',
            value: cartItem?.SubTotalValue,
            items: [
              {
                item_id: cartItem?.ProductSeName,
                item_name: cartItem?.ProductName,
                item_sku: cartItem?.ProductSku,
                price: cartItem?.SubTotalValue,
                quantity: cartItem?.Quantity,
                item_variant: cartItem.Variations?.find((x) => x.IsSelected)?.VariantSku,
                color: cartItem?.AvailableColors?.find((color) => color.OwnColor)?.ColorName,
                size: cartItem.Variations?.find((x) => x.IsSelected)?.ProductAttributeValue,
                item_brand: cartItem?.Brand,
                ...obj,
              },
            ],
          },
        });
      },
      onError: (data: any) => {
        openNotification('danger', 'Add to cart failed.', data.data.Messages[0]);
        // this is to update the selected quantity
        queryClient.invalidateQueries([QueryKeys.cartItems, authObject.session?.accessToken || 'anonymous']);
        queryClient.invalidateQueries([QueryKeys.orderSummary]);
      },
    },
  );
};

export const useEditCartProductMutation = (editCartProductCallback: () => void) => {
  const queryClient = useQueryClient();
  const authObject = useReactQuerySession();
  return useMutation(
    (cartItem: ICartItems) => {
      if (authObject.isAuthenticated) {
        return editCartItemQuantity({ shoppingCartItemId: cartItem.Id, quantity: cartItem.Quantity });
      } else {
        let stringifiedCartItems = localStorage.getItem('cart');
        if (stringifiedCartItems) {
          try {
            let tempCartItems = JSON.parse(stringifiedCartItems) as IGetCartProduct;
            if (tempCartItems.Items) {
              let cartItems: IGetCartProduct = tempCartItems;
              let foundProduct = cartItems.Items.find((x) => x.Id === cartItem.Id);
              if (foundProduct) {
                foundProduct.Quantity = cartItem.Quantity;
                cartItems.SubTotalValue = 0;
                cartItems.TotalValue = 0;
                cartItems.Items.forEach((tempCartItem) => {
                  cartItems.SubTotalValue += tempCartItem.UnitPriceValue * tempCartItem.Quantity;
                  cartItems.TotalValue += tempCartItem.UnitPriceValue * tempCartItem.Quantity;
                });
              }
              localStorage.setItem('cart', JSON.stringify(cartItems));
            } else {
              throw Error('parsed json is corrupted');
            }
          } catch (e) {
            console.log(e, 'json parse error');
          }
        }
        return new Promise((resolve) => resolve('Cart updated locally.'));
      }
    },
    {
      onSuccess: (data) => {
        editCartProductCallback();
        openNotification('success', 'Cart', 'Product quantity updated Successfully');
        queryClient.invalidateQueries([QueryKeys.cartItems, authObject.session?.accessToken || 'anonymous']);
        queryClient.invalidateQueries([QueryKeys.orderSummary]);
      },
      onError: (data: any) => {
        queryClient.invalidateQueries([QueryKeys.cartItems, authObject.session?.accessToken || 'anonymous']);
        queryClient.invalidateQueries([QueryKeys.orderSummary]);
        openNotification('danger', 'Cart', data.data.Messages[0]);
      },
    },
  );
};

export const useDeleteCartProduct = () => {
  const queryClient = useQueryClient();
  const authObject = useReactQuerySession();
  return useMutation(
    (cartItem: ICartItems) => {
      console.log(cartItem);

      if (!authObject.isAuthenticated) {
        let stringifiedCartItems = localStorage.getItem('cart');
        if (stringifiedCartItems) {
          try {
            let tempCartItems = JSON.parse(stringifiedCartItems) as IGetCartProduct;
            if (tempCartItems.Items) {
              let foundProductIndex = tempCartItems.Items.findIndex((x) => x.Id === cartItem.Id);
              if (foundProductIndex > -1) {
                tempCartItems.SubTotalValue -=
                  tempCartItems.Items[foundProductIndex].UnitPriceValue * tempCartItems.Items[foundProductIndex].Quantity;
                tempCartItems.TotalValue -= tempCartItems.Items[foundProductIndex].UnitPriceValue * tempCartItems.Items[foundProductIndex].Quantity;
                tempCartItems.Items.splice(foundProductIndex, 1);
                localStorage.setItem('cart', JSON.stringify(tempCartItems));
              }
              let obj: { [key: string]: string } = {};
              if (cartItem?.Category) {
                let splittedCategories = cartItem?.Category.split(',');
                splittedCategories.forEach((category, categoryIndex) => {
                  obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
                });
              }
              if (!cartItem?.IsGiftVoucher && cartItem?.Gender) {
                obj['gender'] = cartItem.Gender;
              }
              dataLayer.push({ ecommerce: null });
              dataLayer.push({
                event: 'remove_from_cart',
                ecommerce: {
                  currency: 'BDT',
                  value: cartItem?.SubTotalValue,
                  items: [
                    {
                      item_id: cartItem?.ProductSeName,
                      item_name: cartItem?.ProductName,
                      item_sku: cartItem?.ProductSku,
                      price: cartItem?.SubTotalValue,
                      quantity: cartItem?.Quantity,
                      item_variant: cartItem.Variations?.find((x) => x.IsSelected)?.VariantSku,
                      color: cartItem?.AvailableColors?.find((color) => color.OwnColor)?.ColorName,
                      size: cartItem.Variations?.find((x) => x.IsSelected)?.ProductAttributeValue,
                      item_brand: cartItem?.Brand,
                      ...obj,
                    },
                  ],
                },
              });

              return Promise.resolve('Cart updated successfully.');
            } else {
              throw Error('parsed json is corrupted');
            }
          } catch (e) {
            console.log(e, 'json parse error');
            return Promise.reject('Cart updated failed.');
          }
        } else {
          return Promise.reject('Item not found.');
        }
      } else {
        let obj: { [key: string]: string } = {};
        if (cartItem?.Category) {
          let splittedCategories = cartItem?.Category.split(',');
          splittedCategories.forEach((category, categoryIndex) => {
            obj['item_category' + (categoryIndex ? categoryIndex : '')] = category;
          });
        }
        if (!cartItem?.IsGiftVoucher && cartItem?.Gender) {
          obj['gender'] = cartItem?.Gender;
        }
        dataLayer.push({ ecommerce: null });
        dataLayer.push({
          event: 'remove_from_cart',
          ecommerce: {
            currency: 'BDT',
            value: cartItem?.SubTotalValue,
            items: [
              {
                item_id: cartItem?.ProductSeName,
                item_name: cartItem?.ProductName,
                item_sku: cartItem?.ProductSku,
                price: cartItem?.SubTotalValue,
                quantity: cartItem?.Quantity,
                item_variant: cartItem.Variations?.find((x) => x.IsSelected)?.VariantSku,
                color: cartItem?.AvailableColors?.find((color) => color.OwnColor)?.ColorName,
                size: cartItem.Variations?.find((x) => x.IsSelected)?.ProductAttributeValue,
                item_brand: cartItem?.Brand,
                ...obj,
              },
            ],
          },
        });
        return deleteFromCart(cartItem.Id);
      }
    },
    {
      onSuccess: (cartItem: ICartItems) => {
        openNotification('success', 'Cart', 'Product Deleted from Cart Successfully');
        queryClient.invalidateQueries([QueryKeys.orderSummary]);
        queryClient.invalidateQueries([QueryKeys.cartItems, authObject.session?.accessToken || 'anonymous']);
      },
      onError: (data: any) => {
        openNotification('danger', 'Delete cart item failed', data.data.Messages[0]);
      },
    },
  );
};
